import React, { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { useMsal } from "@azure/msal-react";
import { useUser } from "../contexts/UserContext";
import { Plus, Trash2, Save, X } from "lucide-react";
import {
  RoleDto,
  RolePermissionDto,
  RolePermissionType,
  RoleSection,
  createRole,
  deleteRole,
  getRoles,
  updateRole,
} from "../services/roleApiService";

const SECTIONS: RoleSection[] = ["Pending", "Completed", "Deleted", "Batches"];
const ALL_TYPES: RolePermissionType[] = ["Branch", "Warehouse", "VendorId", "FreightVendorId"];
const BATCHES_TYPES: RolePermissionType[] = ["Branch", "Warehouse"];

const typesForSection = (s: RoleSection): RolePermissionType[] =>
  s === "Batches" ? BATCHES_TYPES : ALL_TYPES;

const RolesAdminPage: React.FC = () => {
  const { instance, accounts } = useMsal();
  const { isAdmin } = useUser();

  const [roles, setRoles] = useState<RoleDto[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedId, setSelectedId] = useState<number | "new" | null>(null);

  // Editor state
  const [name, setName] = useState("");
  const [perms, setPerms] = useState<RolePermissionDto[]>([]);
  const [saving, setSaving] = useState(false);
  const [statusMsg, setStatusMsg] = useState<string | null>(null);

  const refresh = async () => {
    if (accounts.length === 0) return;
    setLoading(true);
    try {
      setRoles(await getRoles(instance, accounts[0]));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (isAdmin) refresh(); /* eslint-disable-next-line */ }, [isAdmin, instance, accounts]);

  const startNew = () => {
    setSelectedId("new");
    setName("");
    setPerms([]);
    setStatusMsg(null);
  };

  const selectRole = (r: RoleDto) => {
    setSelectedId(r.id);
    setName(r.name);
    setPerms(r.permissions.map((p) => ({ ...p })));
    setStatusMsg(null);
  };

  const addPerm = (section: RoleSection, type: RolePermissionType, value: string) => {
    const v = value.trim();
    if (!v) return;
    if (perms.some((p) => p.section === section && p.permissionType === type && p.permissionValue === v)) return;
    setPerms((prev) => [...prev, { section, permissionType: type, permissionValue: v }]);
  };

  const removePerm = (idx: number) => setPerms((prev) => prev.filter((_, i) => i !== idx));

  const save = async () => {
    if (!name.trim()) return;
    setSaving(true);
    setStatusMsg(null);
    try {
      if (selectedId === "new") {
        const created = await createRole(instance, accounts[0], { name: name.trim(), permissions: perms });
        await refresh();
        setSelectedId(created.id);
      } else if (typeof selectedId === "number") {
        await updateRole(instance, accounts[0], selectedId, { name: name.trim(), permissions: perms });
        await refresh();
      }
      setStatusMsg("Saved.");
    } catch (e) {
      console.error(e);
      setStatusMsg("Failed to save.");
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (typeof selectedId !== "number") return;
    if (!window.confirm("Delete this role? Users will lose this role.")) return;
    try {
      await deleteRole(instance, accounts[0], selectedId);
      setSelectedId(null);
      setName("");
      setPerms([]);
      await refresh();
    } catch (e) {
      console.error(e);
      setStatusMsg("Failed to delete.");
    }
  };

  const permsBySection = useMemo(() => {
    const m: Record<RoleSection, RolePermissionDto[]> = { Pending: [], Completed: [], Deleted: [], Batches: [] };
    perms.forEach((p) => m[p.section]?.push(p));
    return m;
  }, [perms]);

  if (!isAdmin) {
    return (
      <div className="p-8 text-center">
        <h1 className="text-xl font-bold text-slate-900 dark:text-white">Access Denied</h1>
        <Link to="/" className="text-primary hover:underline mt-4 block">Go to Dashboard</Link>
      </div>
    );
  }

  return (
    <div className="flex-1 overflow-hidden bg-slate-50 dark:bg-background-dark flex">
      {/* Left: roles list */}
      <aside className="w-72 border-r border-slate-200 dark:border-slate-800 bg-white dark:bg-surface-dark flex flex-col">
        <div className="p-4 border-b border-slate-200 dark:border-slate-800 flex items-center justify-between">
          <h1 className="text-lg font-bold text-slate-900 dark:text-white">Roles</h1>
          <button
            onClick={startNew}
            className="flex items-center gap-1 px-2 py-1 text-xs bg-primary hover:bg-primary/90 text-white font-semibold rounded"
          >
            <Plus size={14} /> New
          </button>
        </div>
        <div className="overflow-y-auto flex-1">
          {loading && <div className="p-4 text-sm text-slate-500">Loading…</div>}
          {!loading && roles.length === 0 && (
            <div className="p-4 text-sm text-slate-400 italic">No roles yet.</div>
          )}
          {roles.map((r) => (
            <button
              key={r.id}
              onClick={() => selectRole(r)}
              className={`w-full text-left px-4 py-3 border-b border-slate-100 dark:border-slate-800 text-sm ${
                selectedId === r.id ? "bg-primary/10 text-primary font-bold" : "hover:bg-slate-50 dark:hover:bg-slate-800"
              }`}
            >
              {r.name}
              <div className="text-[11px] text-slate-400 mt-0.5">{r.permissions.length} perms</div>
            </button>
          ))}
        </div>
      </aside>

      {/* Right: editor */}
      <main className="flex-1 overflow-y-auto p-6">
        {selectedId === null ? (
          <div className="text-slate-500 text-sm italic">Select a role to edit, or create a new one.</div>
        ) : (
          <div className="max-w-3xl space-y-6">
            <div className="flex items-center justify-between gap-4">
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Role name"
                className="flex-1 px-3 py-2 text-base font-bold rounded-md border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-900 dark:text-white"
              />
              <button
                onClick={save}
                disabled={saving || !name.trim()}
                className="flex items-center gap-1.5 px-4 py-2 text-sm bg-primary hover:bg-primary/90 disabled:opacity-50 text-white font-semibold rounded-md"
              >
                <Save size={14} /> {saving ? "Saving…" : "Save"}
              </button>
              {typeof selectedId === "number" && (
                <button
                  onClick={remove}
                  className="flex items-center gap-1.5 px-3 py-2 text-sm bg-rose-500 hover:bg-rose-600 text-white font-semibold rounded-md"
                >
                  <Trash2 size={14} /> Delete
                </button>
              )}
            </div>

            {statusMsg && <div className="text-sm text-slate-500">{statusMsg}</div>}

            {SECTIONS.map((section) => (
              <SectionEditor
                key={section}
                section={section}
                perms={permsBySection[section]}
                onAdd={(type, value) => addPerm(section, type, value)}
                onRemove={(p) => removePerm(perms.indexOf(p))}
              />
            ))}
          </div>
        )}
      </main>
    </div>
  );
};

interface SectionEditorProps {
  section: RoleSection;
  perms: RolePermissionDto[];
  onAdd: (type: RolePermissionType, value: string) => void;
  onRemove: (p: RolePermissionDto) => void;
}

const SectionEditor: React.FC<SectionEditorProps> = ({ section, perms, onAdd, onRemove }) => {
  const [type, setType] = useState<RolePermissionType>(typesForSection(section)[0]);
  const [value, setValue] = useState("");
  const types = typesForSection(section);

  return (
    <div className="border border-slate-200 dark:border-slate-800 rounded-xl p-4 bg-white dark:bg-surface-dark">
      <h3 className="text-sm font-bold text-slate-900 dark:text-white mb-3">{section}</h3>

      <div className="flex gap-2 mb-3">
        <select
          value={type}
          onChange={(e) => setType(e.target.value as RolePermissionType)}
          className="px-2 py-1.5 text-sm rounded-md border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-900 dark:text-white"
        >
          {types.map((t) => (
            <option key={t} value={t}>{t}</option>
          ))}
        </select>
        <input
          type="text"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              onAdd(type, value);
              setValue("");
            }
          }}
          placeholder="Value"
          className="flex-1 px-3 py-1.5 text-sm rounded-md border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-900 dark:text-white"
        />
        <button
          onClick={() => { onAdd(type, value); setValue(""); }}
          className="flex items-center gap-1 px-3 py-1.5 text-sm bg-primary hover:bg-primary/90 text-white font-semibold rounded-md"
        >
          <Plus size={14} /> Add
        </button>
      </div>

      {perms.length === 0 ? (
        <div className="text-xs text-slate-400 italic">No permissions.</div>
      ) : (
        <div className="flex flex-wrap gap-1.5">
          {perms.map((p, i) => (
            <span
              key={`${p.permissionType}-${p.permissionValue}-${i}`}
              className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-slate-100 dark:bg-slate-800 text-xs text-slate-700 dark:text-slate-200"
            >
              <span className="font-bold">{p.permissionType}:</span> {p.permissionValue}
              <button
                onClick={() => onRemove(p)}
                className="ml-1 text-slate-400 hover:text-rose-500"
                title="Remove"
              >
                <X size={12} />
              </button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
};

export default RolesAdminPage;
